import { getLegacyMapTypeId } from './default-map-bases.js';
/** @import { MapBase } from './default-map-bases.js' */

/**
 * @typedef {object} WmtsTileMatrixDefinition
 * @property {string} tileMatrix
 * @property {number} minTileRow
 * @property {number} maxTileRow
 * @property {number} minTileColumn
 * @property {number} maxTileColumn
 * @property {number | null} numericZoom Numeric zoom parsed from the tile matrix identifier, or null when absent
 */

/**
 * @typedef {object} WmtsTileMatrixLimit
 * @property {number} minTileRow
 * @property {number} maxTileRow
 * @property {number} minTileColumn
 * @property {number} maxTileColumn
 */

/**
 * @typedef {object} WmtsTileMatrixData
 * @property {WmtsTileMatrixDefinition[]} definitions
 * @property {?Object<string, WmtsTileMatrixLimit>} limits Limits keyed by tile matrix identifier, or null when unbounded
 */

/**
 * @typedef {object} WmtsRequestOptions
 * @property {?string} layer
 * @property {?string} tileMatrixSet
 * @property {string} style
 * @property {?string} format
 */

/**
 * @typedef {object} WmtsLayerData
 * @property {?string[]} tileMatrixLabels Tile matrix identifiers indexed by zoom level, or null when none are known
 * @property {number | null} maximumLevel Deepest zoom level the layer serves, or null when it cannot be determined
 * @property {string} format Image format both the live tiles and the missing tile sample are requested with
 * @property {?string} missingTileSampleUrl GetTile url for a guaranteed missing tile placeholder, or null when none is known
 */

const ENDPOINT_SERVICE_TYPES = ['wms', 'wmts'];

/**
 * Resolves the map base config for a legacy map type id, matching directly or by the slug wms-layers builds.
 * @param {?Object<string, MapBase>} mapBaseConfigs
 * @param {string} legacyMapTypeId
 * @returns {?MapBase}
 */
export function getMapBaseConfigFromLegacyMapTypeId(mapBaseConfigs, legacyMapTypeId) {
  // Ensure there are map base configs
  if (mapBaseConfigs == null) return;

  // First, try to find a direct match for the mapTypeId in the mapBaseConfigs
  const directMatch = mapBaseConfigs[legacyMapTypeId];
  if (directMatch != null) return directMatch;

  // Fall back to matching the slug that wms-layers builds from each config name
  const matchingMapBaseConfigId = Object.keys(mapBaseConfigs).find((key) => {
    const currentMapBaseName = mapBaseConfigs[key].name;
    return getLegacyMapTypeId(currentMapBaseName) == legacyMapTypeId;
  });
  const matchingMapBase = mapBaseConfigs[matchingMapBaseConfigId];
  return matchingMapBase;
}

/** @type {Map<string, WmtsLayerData>} */
const cachedWmtsLayerDataByMapBaseKey = new Map();

/**
 * Returns all direct child elements with the given local name.
 * @param {Element} element
 * @param {string} localName
 * @returns {Element[]}
 */
export function getChildElements(element, localName) {
  if (element == null) return [];
  return Array.from(element.children).filter((child) => child.localName === localName);
}

/**
 * Returns the trimmed text content of the first direct child element with the given local name.
 * @param {Element} element
 * @param {string} localName
 * @returns {?string}
 */
export function getChildElementTextContent(element, localName) {
  const matchingChild = getChildElements(element, localName)[0];
  return matchingChild?.textContent?.trim() ?? null;
}

/**
 * Builds a GetCapabilities request URL for the given protocol.
 * @param {string} url
 * @param {string} serviceType
 * @returns {string}
 */
export function buildGetCapabilitiesRequestUrl(url, serviceType) {
  const capabilitiesUrl = new URL(url);
  const service = serviceType === 'wmts' ? 'WMTS' : 'WMS';
  const version = serviceType === 'wmts' ? '1.0.0' : '1.1.1';
  capabilitiesUrl.searchParams.set('SERVICE', service);
  capabilitiesUrl.searchParams.set('REQUEST', 'GetCapabilities');
  capabilitiesUrl.searchParams.set('VERSION', version);
  return capabilitiesUrl.toString();
}

/**
 * Fetches the text body at the given url.
 * @param {string} url
 * @returns {Promise<string>}
 */
export function fetchCapabilitiesResponse(url) {
  return new Promise((resolve, reject) => {
    const xmlRequest = new XMLHttpRequest();
    xmlRequest.open('GET', url);
    xmlRequest.onload = () => {
      if (xmlRequest.status === 200) resolve(xmlRequest.responseText);
      else reject(new Error(`Request failed with status ${xmlRequest.status}`));
    };
    xmlRequest.onerror = () => reject(new Error('Network error'));
    xmlRequest.onabort = () => reject(new Error('Request aborted'));
    xmlRequest.send();
  });
}

/**
 * Fetches and parses the GetCapabilities document for the given protocol.
 * @param {string} endpoint
 * @param {string} serviceType
 * @returns {Promise<Document>}
 */
export async function fetchCapabilitiesDocument(endpoint, serviceType) {
  const capabilitiesUrl = buildGetCapabilitiesRequestUrl(endpoint, serviceType);
  const xmlCapabilitiesText = await fetchCapabilitiesResponse(capabilitiesUrl);
  return new DOMParser().parseFromString(xmlCapabilitiesText, 'text/xml');
}

/**
 * Parses a WMS GetCapabilities XML document and returns all layers that can be requested.
 * @param {Document} capabilitiesXml
 * @returns {{ id: string, name: string }[]}
 */
export function getAvailableLayersFromWMS(capabilitiesXml) {
  const capabilityElement = capabilitiesXml.getElementsByTagName('Capability')[0];
  const layerElements = capabilityElement?.getElementsByTagName('Layer') ?? [];
  const layers = [];

  for (const layerElement of layerElements) {
    const layerId = getChildElementTextContent(layerElement, 'Name');
    const layerName = getChildElementTextContent(layerElement, 'Title');
    if (!layerId) continue;

    layers.push({ id: layerId, name: layerName });
  }

  return layers;
}

/**
 * Returns the tile matrix set linked to a WMTS layer element.
 * @param {Element} layerElement
 * @returns {?string}
 */
function getWmtsTileMatrixSet(layerElement) {
  const linkElements = layerElement.getElementsByTagName('TileMatrixSetLink');
  if (!linkElements.length) return null;
  return getChildElementTextContent(linkElements[0], 'TileMatrixSet');
}

/**
 * Returns the identifier of the first style on a WMTS layer element, defaulting to default.
 * @param {Element} layerElement
 * @returns {string}
 */
function getWmtsDefaultStyle(layerElement) {
  const styleElement = getChildElements(layerElement, 'Style')[0];
  if (!styleElement) return 'default';
  return getChildElementTextContent(styleElement, 'Identifier') ?? 'default';
}

/**
 * Parses a WMTS GetCapabilities XML document and returns all layers that can be requested.
 * @param {Document} capabilitiesXml
 * @returns {{ id: string, name: string, tileMatrixSet: ?string, style: string, format: string }[]}
 */
export function getAvailableLayersFromWMTS(capabilitiesXml) {
  const contentsElement = capabilitiesXml.getElementsByTagName('Contents')[0];
  const layerElements = contentsElement?.getElementsByTagName('Layer') ?? [];
  const layers = [];

  for (const layerElement of layerElements) {
    const layerId = getChildElementTextContent(layerElement, 'Identifier');
    const layerName = getChildElementTextContent(layerElement, 'Title');
    if (!layerId) continue;

    const tileMatrixSet = getWmtsTileMatrixSet(layerElement);
    const style = getWmtsDefaultStyle(layerElement);
    const format = getChildElementTextContent(layerElement, 'Format') ?? 'image/png';
    layers.push({ id: layerId, name: layerName, tileMatrixSet, style, format });
  }

  return layers;
}

/**
 * Parses the layers from an already fetched GetCapabilities document for the given protocol.
 * @param {Document} capabilitiesDocument
 * @param {string} serviceType
 * @returns {object[]}
 */
export function getLayersFromCapabilities(capabilitiesDocument, serviceType) {
  if (serviceType === 'wmts') return getAvailableLayersFromWMTS(capabilitiesDocument);
  return getAvailableLayersFromWMS(capabilitiesDocument);
}

/**
 * Fetches and parses the layers a candidate protocol advertises.
 * @param {string} endpoint
 * @param {string} serviceType
 * @returns {Promise<?object[]>}
 */
export async function getLayersForServiceType(endpoint, serviceType) {
  try {
    const capabilitiesDocument = await fetchCapabilitiesDocument(endpoint, serviceType);
    return getLayersFromCapabilities(capabilitiesDocument, serviceType);
  } catch {
    return null;
  }
}

/**
 * Queries an endpoint and returns the protocol it speaks along with its layers.
 * @param {string} endpoint
 * @returns {Promise<?{ serviceType: string, layers: object[] }>}
 */
export async function getEndpointCapabilities(endpoint) {
  for (const candidateServiceType of ENDPOINT_SERVICE_TYPES) {
    const layers = await getLayersForServiceType(endpoint, candidateServiceType);
    if (layers?.length) return { serviceType: candidateServiceType, layers };
  }

  return null;
}

/**
 * Attempts to extract a numeric zoom from a tile matrix identifier.
 * @param {string} tileMatrix
 * @returns {number | null}
 */
export function getNumericZoomFromTileMatrix(tileMatrix) {
  if (!tileMatrix) return null;
  if (/^\d+$/.test(tileMatrix)) return Number(tileMatrix);

  const colonSuffixMatch = /:(\d+)$/.exec(tileMatrix);
  if (colonSuffixMatch) return Number(colonSuffixMatch[1]);

  const levelSuffixMatch = /(?:_|:)Level(\d+)$/i.exec(tileMatrix);
  if (levelSuffixMatch) return Number(levelSuffixMatch[1]);

  return null;
}

/**
 * Extract the TileMatrixSet element matching the given identifier from a WMTS capabilities document.
 * @param {Element} capabilitiesContent
 * @param {string} tileMatrixSetIdentifier
 * @returns {Element[]}
 */
function getTileMatrixElementsFromCapabilities(capabilitiesContent, tileMatrixSetIdentifier) {
  const tileMatrixSetElements = getChildElements(capabilitiesContent, 'TileMatrixSet');
  const tileMatrixSet = tileMatrixSetElements.find(
    (element) => getChildElementTextContent(element, 'Identifier') === tileMatrixSetIdentifier
  );
  if (!tileMatrixSet) return [];

  // Get the TileMatrix elements within the matching TileMatrixSet and extract their definitions.
  const tileMatrixElements = getChildElements(tileMatrixSet, 'TileMatrix');
  return tileMatrixElements;
}

/**
 * Parses WMTS tile matrix definitions with optional matrix limits from a capabilities document.
 * @param {Document} capabilitiesDocument
 * @param {string} layerIdentifier
 * @param {string} tileMatrixSetIdentifier
 * @returns {?WmtsTileMatrixData}
 */
export function getWmtsLayerTileMatrixData(capabilitiesDocument, layerIdentifier, tileMatrixSetIdentifier) {
  const capabilitiesContent = Array.from(capabilitiesDocument.getElementsByTagName('*')).find(
    (element) => element.localName === 'Contents'
  );
  if (!capabilitiesContent) return null;

  // Map each TileMatrix element to its definition, including tile limits and numeric zoom.
  const tileMatrixElements = getTileMatrixElementsFromCapabilities(capabilitiesContent, tileMatrixSetIdentifier);
  const unsanitizedMatrixDefinitions = tileMatrixElements.map((tileMatrixElement) => {
    // Ensure that the TileMatrix element has a valid identifier, matrix width, and matrix height.
    const tileMatrix = getChildElementTextContent(tileMatrixElement, 'Identifier');
    const matrixWidth = Number(getChildElementTextContent(tileMatrixElement, 'MatrixWidth'));
    const matrixHeight = Number(getChildElementTextContent(tileMatrixElement, 'MatrixHeight'));
    if (!tileMatrix || !Number.isFinite(matrixWidth) || !Number.isFinite(matrixHeight)) return null;

    // Build the tile matrix definition
    const tileMatrixZoom = getNumericZoomFromTileMatrix(tileMatrix);
    return {
      tileMatrix,
      minTileRow: 0,
      maxTileRow: Math.max(0, matrixHeight - 1),
      minTileColumn: 0,
      maxTileColumn: Math.max(0, matrixWidth - 1),
      numericZoom: tileMatrixZoom
    };
  });
  const matrixDefinitions = unsanitizedMatrixDefinitions.filter(Boolean);

  // Filter the base definitions to the requested layer's coverage and return them alongside its limits
  const tileMatrixData = buildAllTileMatrixDataForLayer(matrixDefinitions, capabilitiesContent, layerIdentifier, tileMatrixSetIdentifier);
  return tileMatrixData;
}

/**
 * Indexes a layer's tile limits by tile matrix identifier, or null when the layer declares no limits for the set.
 * @param {Element} matchingLayer
 * @param {string} tileMatrixSetIdentifier
 * @returns {?object}
 */
function getTileMatrixLimitsForLayer(matchingLayer, tileMatrixSetIdentifier) {
  // Find the link from the layer to the requested tile matrix set
  const layerLinkElements = getChildElements(matchingLayer, 'TileMatrixSetLink');
  const tileMatrixSetLinkForLayer = layerLinkElements.find((tileMatrixSetLinkElement) => {
    return getChildElementTextContent(tileMatrixSetLinkElement, 'TileMatrixSet') === tileMatrixSetIdentifier;
  });

  // When the link declares no limits, the layer covers the whole set
  const tileMatrixSetLimitsForLayer = getChildElements(tileMatrixSetLinkForLayer, 'TileMatrixSetLimits')[0];
  if (!tileMatrixSetLimitsForLayer) return null;

  // Map each limit to it's tile matrix identifier
  const organizedTileMatrixLimits = organizeLimitsByTheirTileMatrix(tileMatrixSetLimitsForLayer);
  return organizedTileMatrixLimits;
}

/**
 * Indexes the tile limits declared under a TileMatrixSetLimits element by their tile matrix identifier.
 * @param {Element} tileMatrixSetLimits
 * @returns {object}
 */
function organizeLimitsByTheirTileMatrix(tileMatrixSetLimits) {
  // Build the limits by tile matrix mapping
  const limitEntries = getChildElements(tileMatrixSetLimits, 'TileMatrixLimits');
  const limitsByTileMatrix = {};
  for (const limitElement of limitEntries) {
    // Get all the content for the limit
    const tileMatrix = getChildElementTextContent(limitElement, 'TileMatrix');
    const minTileRow = Number(getChildElementTextContent(limitElement, 'MinTileRow'));
    const maxTileRow = Number(getChildElementTextContent(limitElement, 'MaxTileRow'));
    const minTileColumn = Number(getChildElementTextContent(limitElement, 'MinTileCol'));
    const maxTileColumn = Number(getChildElementTextContent(limitElement, 'MaxTileCol'));

    // Skip this limit if it doesn't have a valid tile matrix identifier
    if (!tileMatrix) continue;

    // Ensure all limit values are finite numbers
    const limitValuesAreValid = [minTileRow, maxTileRow, minTileColumn, maxTileColumn].every((value) => Number.isFinite(value));
    if (!limitValuesAreValid) continue;

    // Build the limit and set it
    const limit = { minTileRow, maxTileRow, minTileColumn, maxTileColumn };
    limitsByTileMatrix[tileMatrix] = limit;
  }

  return limitsByTileMatrix;
}

/**
 * Returns the layer's tile matrix definitions alongside the tile limits that bound them, when the layer declares any.
 * @param {object[]} matrixDefinitions
 * @param {Element} capabilitiesContent
 * @param {string} layerIdentifier
 * @param {string} tileMatrixSetIdentifier
 * @returns {WmtsTileMatrixData}
 */
function buildAllTileMatrixDataForLayer(matrixDefinitions, capabilitiesContent, layerIdentifier, tileMatrixSetIdentifier) {
  // If a layer doesn't match the identifier, then all matrix definitions are valid and unbounded
  const layerElements = getChildElements(capabilitiesContent, 'Layer');
  const matchingLayer = layerElements.find((layerElement) => getChildElementTextContent(layerElement, 'Identifier') === layerIdentifier);
  if (!matchingLayer) return { definitions: matrixDefinitions, limits: null };

  // If there are no limits for the layer, all matrix definitions are valid and unbounded
  const limits = getTileMatrixLimitsForLayer(matchingLayer, tileMatrixSetIdentifier);
  if (!limits) return { definitions: matrixDefinitions, limits: null };

  // Keep only the definitions the layer's limits cover and return them with those limits
  const definitions = matrixDefinitions.filter((matrixDefinition) => limits[matrixDefinition.tileMatrix]);
  return { definitions, limits };
}

/**
 * Extracts the WMTS request parameters embedded in a saved map base url.
 * @param {string} url
 * @returns {WmtsRequestOptions}
 */
export function getWmtsRequestOptionsFromUrl(url) {
  // Read the WMTS parameters Cesium needs from the url query string
  const wmtsEndpoint = new URL(url);
  const layer = wmtsEndpoint.searchParams.get('layer');
  const tileMatrixSet = wmtsEndpoint.searchParams.get('tilematrixset');
  const style = wmtsEndpoint.searchParams.get('style') || 'default';
  const format = wmtsEndpoint.searchParams.get('format');

  return { layer, tileMatrixSet, style, format };
}

/**
 * Resolves WMTS layer data once per map base, reusing the result so engines do not refetch capabilities.
 * @param {string} mapBaseKey
 * @param {string} url
 * @returns {Promise<WmtsLayerData>}
 */
export async function getWmtsLayerData(mapBaseKey, url) {
  // Reuse a previously resolved result, including negative ones, so repeated switches stay cheap
  const cachedLayerData = cachedWmtsLayerDataByMapBaseKey.get(mapBaseKey);
  if (cachedLayerData) return cachedLayerData;

  // Derive the layer data from the source capabilities and cache it
  const layerData = await fetchWmtsLayerData(url);
  cachedWmtsLayerDataByMapBaseKey.set(mapBaseKey, layerData);
  return layerData;
}

/**
 * Resolves the data an engine needs to render a WMTS layer, fetching the source capabilities a single time.
 * @param {string} url
 * @returns {Promise<WmtsLayerData>}
 */
export async function fetchWmtsLayerData(url) {
  // Resolve the format the provider requests live tiles with from the saved request options
  const requestOptions = getWmtsRequestOptionsFromUrl(url);
  const format = requestOptions.format || 'image/png';
  const fallbackLayerData = { tileMatrixLabels: null, maximumLevel: null, format, missingTileSampleUrl: null };

  // Without a layer and tile matrix set there is nothing to derive from the capabilities
  if (!requestOptions.layer || !requestOptions.tileMatrixSet) return fallbackLayerData;

  try {
    // Fetch and parse the capabilities once, deriving every WMTS detail from that single document
    const capabilitiesDocument = await fetchCapabilitiesDocument(url, 'wmts');
    const tileMatrixData = getWmtsLayerTileMatrixData(capabilitiesDocument, requestOptions.layer, requestOptions.tileMatrixSet);
    if (!tileMatrixData?.definitions?.length) return fallbackLayerData;

    // Label each tile matrix by its level and cap requests at the deepest level the source serves
    const tileMatrixLabels = buildTileMatrixLabels(tileMatrixData.definitions);
    const maximumLevel = getMaximumWmtsLevel(tileMatrixData.definitions);

    // Resolve a guaranteed missing tile placeholder so the caller can build a discard policy for it
    const deepestTileMatrixData = getDeepestTileMatrixData(tileMatrixData);
    const missingTileSampleUrl = await getMissingTileSampleUrl(url, deepestTileMatrixData);

    return { tileMatrixLabels, maximumLevel, format, missingTileSampleUrl };
  } catch {
    // Treat any fetch or parse failure as a layer with no derivable detail so the provider still renders
    return fallbackLayerData;
  }
}

/**
 * Builds tile matrix identifiers indexed by zoom level so Cesium requests the source's own identifier per level.
 * @param {WmtsTileMatrixDefinition[]} definitions
 * @returns {string[]}
 */
function buildTileMatrixLabels(definitions) {
  // Without parseable zooms, fall back to the declared order so labels still map to ascending levels
  const definitionsWithZoom = definitions.filter((definition) => definition.numericZoom != null);
  if (!definitionsWithZoom.length) return definitions.map((definition) => definition.tileMatrix);

  // Place each identifier at its numeric zoom so the array index matches the tiling scheme level
  const deepestZoom = Math.max(...definitionsWithZoom.map((definition) => definition.numericZoom));
  const tileMatrixLabels = new Array(deepestZoom + 1).fill(undefined);
  definitionsWithZoom.forEach((definition) => {
    tileMatrixLabels[definition.numericZoom] = definition.tileMatrix;
  });
  return tileMatrixLabels;
}

/**
 * Returns the deepest level the layer serves, by numeric zoom when known and by declared order otherwise.
 * @param {WmtsTileMatrixDefinition[]} definitions
 * @returns {number | null}
 */
function getMaximumWmtsLevel(definitions) {
  // Prefer the deepest declared numeric zoom since it aligns with the tiling scheme level
  const numericZooms = definitions.map((definition) => definition.numericZoom).filter((numericZoom) => numericZoom != null);
  if (numericZooms.length) return Math.max(...numericZooms);

  // Without numeric zooms, the last declared matrix is the deepest level
  if (definitions.length) return definitions.length - 1;
  return null;
}

/**
 * Pairs the deepest tile matrix the layer declares with that matrix's limit, or null when none declare a zoom.
 * @param {WmtsTileMatrixData} tileMatrixData
 * @returns {?{ definition: WmtsTileMatrixDefinition, limit: ?WmtsTileMatrixLimit }}
 */
function getDeepestTileMatrixData(tileMatrixData) {
  const definition = getDeepestTileMatrixDefinition(tileMatrixData.definitions);
  if (!definition) return null;

  const limit = tileMatrixData.limits?.[definition.tileMatrix] ?? null;
  return { definition, limit };
}

/**
 * Builds the GetTile url for a tile guaranteed to be a missing tile placeholder, or null when none can be found.
 * @param {string} url
 * @param {?{ definition: WmtsTileMatrixDefinition, limit: ?WmtsTileMatrixLimit }} deepestTileMatrixData
 * @returns {Promise<string | null>}
 */
async function getMissingTileSampleUrl(url, deepestTileMatrixData) {
  if (!deepestTileMatrixData) return null;

  // When the layer declares a limit, a tile outside it is guaranteed missing and is the most reliable sample
  const { definition, limit } = deepestTileMatrixData;
  if (limit != null) {
    const tileOutsideLimits = getTileOutsideLimits(definition, limit);
    if (tileOutsideLimits) return buildSampleUrlForTile(url, definition, tileOutsideLimits);
  }

  // If the limits don't provide a reliable missing tile, only sample when the deepest matrix is a shared placeholder
  const deepestTileIsRealImagery = await checkIfDeepestTileIsRealImagery(url, definition);
  if (deepestTileIsRealImagery) return null;

  // Build the url for the deepest tile definition using a corner tile
  return buildSampleUrlForTile(url, definition, { tileRow: 0, tileColumn: 0 });
}

/**
 * Returns a tile inside the matrix but outside the layer's declared limits, or null when the limits cover it all.
 * @param {WmtsTileMatrixDefinition} definition
 * @param {?WmtsTileMatrixLimit} limit
 * @returns {?{ tileRow: number, tileColumn: number }}
 */
function getTileOutsideLimits(definition, limit) {
  // A matrix with no limit entry is fully covered, so no excluded tile exists
  if (!limit) return null;

  // Prefer a column just past the covered range, then a row, so the sampled tile is one the layer does not cover
  if (limit.maxTileColumn < definition.maxTileColumn) return { tileRow: limit.minTileRow, tileColumn: limit.maxTileColumn + 1 };
  if (limit.minTileColumn > 0) return { tileRow: limit.minTileRow, tileColumn: limit.minTileColumn - 1 };
  if (limit.maxTileRow < definition.maxTileRow) return { tileRow: limit.maxTileRow + 1, tileColumn: limit.minTileColumn };
  if (limit.minTileRow > 0) return { tileRow: limit.minTileRow - 1, tileColumn: limit.minTileColumn };

  // The limit spans the entire matrix, so there is no tile outside it to sample
  return null;
}

/**
 * Checks whether the deepest matrix holds real imagery by comparing spread sample tiles, assuming real imagery on failure.
 * @param {string} url
 * @param {WmtsTileMatrixDefinition} definition
 * @returns {Promise<boolean>}
 */
async function checkIfDeepestTileIsRealImagery(url, definition) {
  // Sample three spread tiles so a uniform real region is not mistaken for a shared placeholder
  const centerTileRow = Math.floor(definition.maxTileRow / 2);
  const centerTileColumn = Math.floor(definition.maxTileColumn / 2);
  const sampleTiles = [
    { tileRow: 0, tileColumn: 0 },
    { tileRow: definition.maxTileRow, tileColumn: definition.maxTileColumn },
    { tileRow: centerTileRow, tileColumn: centerTileColumn }
  ];

  try {
    // Identical bytes across distant tiles mean a shared placeholder, while any difference means real imagery
    const sampleUrls = sampleTiles.map((tile) => buildSampleUrlForTile(url, definition, tile));
    const sampleByteBuffers = await Promise.all(sampleUrls.map((sampleUrl) => fetchTileBytes(sampleUrl)));
    if (sampleByteBuffers.some((byteBuffer) => byteBuffer == null)) return true;
    return !allByteBuffersEqual(sampleByteBuffers);
  } catch {
    // Treat a failed result as real imagery so a discard policy is never built from a bad sample
    return true;
  }
}

/**
 * Builds a GetTile url for a single sample tile within the given tile matrix.
 * @param {string} url
 * @param {WmtsTileMatrixDefinition} definition
 * @param {{ tileRow: number, tileColumn: number }} tile
 * @returns {string}
 */
function buildSampleUrlForTile(url, definition, tile) {
  const requestOptions = getWmtsRequestOptionsFromUrl(url);
  return buildWmtsGetTileUrl(url, {
    layer: requestOptions.layer,
    style: requestOptions.style,
    format: requestOptions.format || 'image/png',
    tileMatrixSet: requestOptions.tileMatrixSet,
    tileMatrix: definition.tileMatrix,
    tileRow: tile.tileRow,
    tileColumn: tile.tileColumn
  });
}

/**
 * Fetches the raw bytes of a tile, or null when the request fails.
 * @param {string} tileUrl
 * @returns {Promise<?Uint8Array>}
 */
async function fetchTileBytes(tileUrl) {
  const tileResponse = await fetch(tileUrl);
  if (!tileResponse.ok) return null;

  const tileArrayBuffer = await tileResponse.arrayBuffer();
  return new Uint8Array(tileArrayBuffer);
}

/**
 * Reports whether two byte buffers hold exactly the same bytes.
 * @param {Uint8Array} firstBytes
 * @param {Uint8Array} secondBytes
 * @returns {boolean}
 */
function areByteBuffersEqual(firstBytes, secondBytes) {
  if (firstBytes.length !== secondBytes.length) return false;
  return firstBytes.every((byteValue, byteIndex) => byteValue === secondBytes[byteIndex]);
}

/**
 * Reports whether every byte buffer in the list holds exactly the same bytes.
 * @param {Uint8Array[]} byteBuffers
 * @returns {boolean}
 */
function allByteBuffersEqual(byteBuffers) {
  const [firstByteBuffer, ...remainingByteBuffers] = byteBuffers;
  return remainingByteBuffers.every((byteBuffer) => areByteBuffersEqual(firstByteBuffer, byteBuffer));
}

/**
 * Returns the tile matrix definition with the deepest numeric zoom, or null when none declare one.
 * @param {WmtsTileMatrixDefinition[]} tileMatrixDefinitions
 * @returns {?WmtsTileMatrixDefinition}
 */
function getDeepestTileMatrixDefinition(tileMatrixDefinitions) {
  // Keep only the definitions that declare a usable numeric zoom level
  const definitionsWithZoom = tileMatrixDefinitions.filter((definition) => definition.numericZoom != null);
  if (!definitionsWithZoom.length) return null;

  // Find the deepest zoom
  const deepestZoom = Math.max(...definitionsWithZoom.map((definition) => definition.numericZoom));
  return definitionsWithZoom.find((definition) => definition.numericZoom === deepestZoom);
}

/**
 * Builds a WMTS GetTile request url for a single tile, used to sample a source's missing tile placeholder.
 * @param {string} url
 * @param {object} tileRequest
 * @returns {string}
 */
function buildWmtsGetTileUrl(url, tileRequest) {
  // Start from the saved endpoint and drop the params that the GetTile request will set itself
  const tileUrl = new URL(url);
  const reservedParameters = [
    'service',
    'request',
    'version',
    'layer',
    'style',
    'format',
    'tilematrixset',
    'tilematrix',
    'tilerow',
    'tilecol'
  ];
  reservedParameters.forEach((parameterName) => tileUrl.searchParams.delete(parameterName));

  // Set the GetTile request params that select the single tile to sample
  tileUrl.searchParams.set('SERVICE', 'WMTS');
  tileUrl.searchParams.set('REQUEST', 'GetTile');
  tileUrl.searchParams.set('VERSION', '1.0.0');
  tileUrl.searchParams.set('LAYER', tileRequest.layer);
  tileUrl.searchParams.set('STYLE', tileRequest.style);
  tileUrl.searchParams.set('FORMAT', tileRequest.format);
  tileUrl.searchParams.set('TILEMATRIXSET', tileRequest.tileMatrixSet);
  tileUrl.searchParams.set('TILEMATRIX', tileRequest.tileMatrix);
  tileUrl.searchParams.set('TILEROW', String(tileRequest.tileRow));
  tileUrl.searchParams.set('TILECOL', String(tileRequest.tileColumn));

  return tileUrl.toString();
}

/**
 * Returns the ordered time values to try for a WMTS layer, preferring the default then each range end.
 * @param {Document} capabilitiesDocument
 * @param {string} layerIdentifier
 * @returns {string[]} The ordered time values, or an empty array when the layer has no time dimension.
 */
export function getWmtsTimeDimensionValues(capabilitiesDocument, layerIdentifier) {
  // Locate the requested layer within the capabilities contents
  const contentsElement = Array.from(capabilitiesDocument.getElementsByTagName('*')).find((element) => element.localName === 'Contents');
  if (!contentsElement) return [];

  const layerElements = getChildElements(contentsElement, 'Layer');
  const matchingLayer = layerElements.find((layerElement) => getChildElementTextContent(layerElement, 'Identifier') === layerIdentifier);
  if (!matchingLayer) return [];

  // Find the time dimension on the layer, returning none when the layer is not temporal
  const dimensionElements = getChildElements(matchingLayer, 'Dimension');
  const timeDimension = dimensionElements.find((element) => getChildElementTextContent(element, 'Identifier')?.toLowerCase() === 'time');
  if (!timeDimension) return [];

  // Prefer the advertised default, then fall back to the end of each available range from newest to oldest
  const defaultValue = getChildElementTextContent(timeDimension, 'Default');
  const rangeEndValues = getWmtsTimeRangeEndValues(timeDimension);
  const orderedValues = [defaultValue, ...rangeEndValues].filter(Boolean);

  return Array.from(new Set(orderedValues));
}

/**
 * Returns the end of each time range on a WMTS time dimension, ordered newest range first.
 * @param {Element} timeDimension
 * @returns {string[]}
 */
function getWmtsTimeRangeEndValues(timeDimension) {
  const valueElements = getChildElements(timeDimension, 'Value');
  const rangeEndValues = valueElements.map((valueElement) => {
    const rawValue = valueElement.textContent?.trim();
    if (!rawValue) return null;

    // A range is written as start/end/period, while a single value is used as is
    const valueParts = rawValue.split('/');
    if (valueParts.length >= 2) return valueParts[1];
    return rawValue;
  });

  return rangeEndValues.filter(Boolean).reverse();
}
